import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppDispatch } from '@redux/hooks';
import {
  setOrganizationData,
  clearOrganizationData,
} from '@redux/reducers/organization.reducer';
import { setLocale } from '@redux/reducers/locale.reducer';
import {
  logoutUser,
  setUserData,
  setUserDetails,
} from '@redux/reducers/user.reducer';
import { getSelfWithOrganizationRequest, UserLocale } from '@models/user.model';
import { graphQlClient } from '@config/graphqlClient';
import UserGraphQL from '@graphql/user.queries';
import authService from '@services/auth';
import {
  setTokenLocalStorage,
  setTokenSessionStorage,
  removeResetPasswordEmail,
} from '@config/storage';
import { toast } from 'react-toastify';
import { useTranslation } from '@hooks/useTranslation';
/** The literal the backend returns on a failed sign-in; never shown to the user as-is. */
export const AUTH_FAILED_BACKEND_MESSAGE = 'Authentication failed';

type LoginRequiresPasswordChange = {
  status: 'REQUIRES_PASSWORD_CHANGE';
  confirmToken: string;
};
type LoginSuccess = {
  status: 'SUCCESS';
};
type LoginFailure = { status: 'FAILURE'; errorMessage?: string };
type LoginResponse = LoginRequiresPasswordChange | LoginSuccess | LoginFailure;

export type SignInParams = {
  user: string;
  password: string;
  rememberMe: boolean;
};

type LoginResult = {
  signIn: (params: SignInParams) => Promise<void | LoginResponse>;
  isLoading: boolean;
  confirmInitialSignIn: (
    email: string,
    password: string,
    confirmToken: string,
    rememberMe: boolean,
  ) => Promise<void | LoginResponse>;
};

export const useLogin = (): LoginResult => {
  const tActionMessages = useTranslation('actionMessages');
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);

  const handleTokenLogin = useCallback(
    async (token: string, rememberMe: boolean) => {
      // Clear any stored reset password email on successful login
      removeResetPasswordEmail();
      // Persist token (and rememberMe) in Redux and storage
      dispatch(setUserData(token, rememberMe));
      // Write token to both storages to support early requests before Redux rehydration in new tabs
      setTokenLocalStorage(token);
      setTokenSessionStorage(token);
      // Re-arm the dashboard's post-login "empty → first project" redirect.
      sessionStorage.removeItem('dashboardLandingHandled');

      // Fetch user + org using GraphQL
      const userData: getSelfWithOrganizationRequest =
        await graphQlClient.request(
          UserGraphQL.queries.getSelfWithOrganization,
        );

      // Populate Redux state - user details and locale
      dispatch(setUserDetails(userData.getSelfWithOrganization.user));
      dispatch(
        setLocale(
          userData.getSelfWithOrganization.user.locale || UserLocale.EN,
        ),
      );

      // Check if user has an organization
      if (!userData.getSelfWithOrganization.organization) {
        // Clear organization data from storage
        dispatch(clearOrganizationData());
        // Clear any redirect URL to prevent interference
        sessionStorage.removeItem('redirectUrl');
        history.push('/no-organization');
        return;
      }

      // User has organization - set it in Redux
      dispatch(
        setOrganizationData(userData.getSelfWithOrganization.organization),
      );

      // Redirect to saved URL or home
      const from = sessionStorage.getItem('redirectUrl') || '/';
      sessionStorage.removeItem('redirectUrl');
      if (from && from !== '/logout') {
        history.push(from);
      }
    },
    [dispatch, history],
  );

  const signIn = useCallback(
    async (params: SignInParams) => {
      setIsLoading(true);
      try {
        const data = await authService.signIn({
          user: params.user,
          password: params.password,
        });

        if (data?.message === 'FORCE_CHANGE_PASSWORD' && data?.confirmToken) {
          setIsLoading(false);
          toast.warning(tActionMessages.pleaseChangeYourPassword);
          const resp: LoginResponse = {
            status: 'REQUIRES_PASSWORD_CHANGE',
            confirmToken: data.confirmToken,
          };
          return resp;
        }

        if (data?.token) {
          // Clear any stored reset password email on successful login
          removeResetPasswordEmail();
          // Persist token first
          dispatch(setUserData(data.token, params.rememberMe));
          setTokenLocalStorage(data.token);
          setTokenSessionStorage(data.token);
          // Re-arm the dashboard's post-login "empty → first project" redirect.
          sessionStorage.removeItem('dashboardLandingHandled');

          // Fetch user data with default organization (backend handles default org selection)
          try {
            const userData: getSelfWithOrganizationRequest =
              await graphQlClient.request(
                UserGraphQL.queries.getSelfWithOrganization,
              );

            // Set user details and organization
            dispatch(setUserDetails(userData.getSelfWithOrganization.user));
            dispatch(
              setLocale(
                userData.getSelfWithOrganization.user.locale || UserLocale.EN,
              ),
            );

            // Check if user has an organization
            if (!userData.getSelfWithOrganization.organization) {
              // Clear organization data from storage
              dispatch(clearOrganizationData());
              setIsLoading(false);
              toast.success(tActionMessages.loggedInSuccessfully);
              // Clear any redirect URL to prevent interference
              sessionStorage.removeItem('redirectUrl');
              history.push('/no-organization');
              const resp: LoginResponse = { status: 'SUCCESS' };
              return resp;
            }

            // User has organization - set it in Redux
            dispatch(
              setOrganizationData(
                userData.getSelfWithOrganization.organization,
              ),
            );
            setIsLoading(false);
            toast.success(tActionMessages.loggedInSuccessfully);

            // Redirect to saved URL or home
            const from = sessionStorage.getItem('redirectUrl') || '/';
            sessionStorage.removeItem('redirectUrl');
            if (from && from !== '/logout') {
              history.push(from);
            }

            const resp: LoginResponse = { status: 'SUCCESS' };
            return resp;
          } catch (userError: any) {
            try {
              const tokenPayload = JSON.parse(atob(data.token.split('.')[1]));
              dispatch(
                setUserDetails({
                  email: tokenPayload.email,
                  firstName: tokenPayload.given_name || '',
                  lastName: tokenPayload.family_name || '',
                  userId: tokenPayload.sub,
                }),
              );
              // Clear organization data from storage
              dispatch(clearOrganizationData());
              setIsLoading(false);
              toast.success(tActionMessages.loggedInSuccessfully);
              // Clear any redirect URL to prevent interference
              sessionStorage.removeItem('redirectUrl');
              history.push('/no-organization');
              const resp: LoginResponse = { status: 'SUCCESS' };
              return resp;
            } catch (decodeError) {
              toast.error(tActionMessages.errorLoadingUserData);
              setIsLoading(false);
              const resp: LoginResponse = {
                status: 'FAILURE',
                errorMessage: 'Error loading user data',
              };
              return resp;
            }
          }
        }

        // The backend's raw "Authentication failed" is not user copy; show the translated one.
        const errorMessage =
          data?.errorMessage === AUTH_FAILED_BACKEND_MESSAGE
            ? tActionMessages.checkYourCredentials
            : data?.errorMessage || tActionMessages.requestError;

        toast.error(errorMessage);
        setIsLoading(false);
        const resp: LoginResponse = { status: 'FAILURE', errorMessage };
        return resp;
      } catch (e: any) {
        const errorMessage =
          e?.response?.data?.errorMessage || e?.message || tActionMessages.requestError;
        toast.error(tActionMessages.checkYourCredentials);
        setIsLoading(false);
        const resp: LoginResponse = { status: 'FAILURE', errorMessage };
        return resp;
      }
    },
    [
      dispatch,
      history,
      tActionMessages.checkYourCredentials,
      tActionMessages.errorLoadingUserData,
      tActionMessages.loggedInSuccessfully,
      tActionMessages.pleaseChangeYourPassword,
      tActionMessages.requestError,
    ],
  );

  const confirmInitialSignIn = useCallback(
    async (
      email: string,
      password: string,
      confirmToken: string,
      rememberMe: boolean,
    ) => {
      setIsLoading(true);
      try {
        const data = await authService.signInWithConfirm(
          email,
          password,
          confirmToken,
        );
        if (data?.token) {
          await handleTokenLogin(data.token, rememberMe);
          setIsLoading(false);
          toast.success(tActionMessages.passwordChangeSuccessful);
          const resp: LoginResponse = { status: 'SUCCESS' };
          return resp;
        }

        const errorMessage = data?.errorMessage || tActionMessages.requestError;
        toast.error(errorMessage);

        setIsLoading(false);
        const resp: LoginResponse = { status: 'FAILURE', errorMessage };
        return resp;
      } catch (e: any) {
        const errorMessage =
          e?.response?.data?.errorMessage || e?.message || tActionMessages.requestError;
        toast.error(tActionMessages.checkYourCredentials);
        dispatch(logoutUser());
        setIsLoading(false);
        const resp: LoginResponse = { status: 'FAILURE', errorMessage };
        return resp;
      }
    },
    [
      dispatch,
      handleTokenLogin,
      tActionMessages.passwordChangeSuccessful,
      tActionMessages.checkYourCredentials,
      tActionMessages.requestError,
    ],
  );

  return { signIn, confirmInitialSignIn, isLoading };
};

export default useLogin;
